import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  createChatMember,
  deleteChat,
  deleteChatMember,
  getOrganization,
  getOrganizationChats,
} from "../../api/api";
import NewOrgChannelModal from "../organization_channels_page/NewOrgChannelModal";
import MultiUserSearch from "../messaging_page/MultiUserSearch";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { cn } from "../../lib/utils";

/**
 * Organization admin: manage channels (create/delete, add/remove members).
 * @param {Object} props
 * @param {number} props.userId - Organization account id
 * @param {object} [props.user]
 * @returns {JSX.Element}
 */
function ChannelsTab({ userId, user }) {
  const orgId = userId;
  const [chats, setChats] = useState([]);
  const [affiliations, setAffiliations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState(false);
  /** @type {[object|null, function]} */
  const [confirmDeleteChat, setConfirmDeleteChat] = useState(null);
  /** @type {[object|null, function]} */
  const [confirmRemoveMember, setConfirmRemoveMember] = useState(null);

  const candidateUsers = useMemo(
    () => affiliations.map((a) => a.user).filter(Boolean),
    [affiliations]
  );

  const selectedChat = useMemo(
    () => chats.find((c) => Number(c.id) === Number(selectedChatId)) || null,
    [chats, selectedChatId]
  );

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [orgRes, chatsRes] = await Promise.all([
        getOrganization(orgId, orgId),
        getOrganizationChats(orgId, orgId),
      ]);
      if (orgRes.success && orgRes.data) {
        setAffiliations(
          (orgRes.data.affiliations || []).filter((a) => a.status === "active")
        );
      }
      if (chatsRes.success && Array.isArray(chatsRes.data)) {
        setChats(chatsRes.data);
        setSelectedChatId((prev) => {
          if (prev && chatsRes.data.some((c) => Number(c.id) === Number(prev))) {
            return prev;
          }
          const general = chatsRes.data.find((c) => c.is_default);
          return general?.id || chatsRes.data[0]?.id || null;
        });
      } else {
        setChats([]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Delete the channel pending confirmation.
   * @returns {Promise<void>}
   */
  const handleConfirmDelete = async () => {
    const chat = confirmDeleteChat;
    if (!chat || chat.is_default) return;
    setBusy(true);
    try {
      const res = await deleteChat(chat.id);
      if (res.success) {
        setConfirmDeleteChat(null);
        await load();
      } else {
        alert(res.error || "Failed to delete channel");
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * Remove the member pending confirmation.
   * @returns {Promise<void>}
   */
  const handleConfirmRemoveMember = async () => {
    if (!selectedChat || !confirmRemoveMember) return;
    setBusy(true);
    try {
      const res = await deleteChatMember(selectedChat.id, confirmRemoveMember.user_id);
      if (res.success) {
        setConfirmRemoveMember(null);
        await load();
      } else {
        alert(res.error || "Failed to remove member");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleAddMembers = async (selectedUsers) => {
    if (!selectedChat || selectedUsers.length === 0) return;
    setBusy(true);
    try {
      let ok = 0;
      for (const u of selectedUsers) {
        const res = await createChatMember(selectedChat.id, {
          user_id: u.id,
          role: "member",
        });
        if (res.success) ok += 1;
      }
      if (ok > 0) {
        setShowAdd(false);
        await load();
      } else {
        alert("Failed to add members");
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-text-muted">Loading channels…</p>;
  }

  const members = selectedChat?.other_members || [];
  const memberIds = new Set(members.map((m) => m.user_id));
  const addCandidates = candidateUsers.filter((u) => !memberIds.has(u.id));
  const removeMemberName =
    confirmRemoveMember?.name ||
    confirmRemoveMember?.username ||
    `User ${confirmRemoveMember?.user_id ?? ""}`;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">Channels</h2>
          <p className="text-sm text-text-muted">
            Create channels and manage membership for {user?.name || "your organization"}.
          </p>
        </div>
        <Button type="button" size="sm" onClick={() => setShowNew(true)}>
          New channel
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[14rem_minmax(0,1fr)]">
        <nav className="flex flex-col gap-1" aria-label="Channels">
          {chats.map((chat) => {
            const active = Number(chat.id) === Number(selectedChatId);
            return (
              <button
                key={chat.id}
                type="button"
                onClick={() => {
                  setSelectedChatId(chat.id);
                  setShowAdd(false);
                }}
                className={cn(
                  "rounded-lg px-3 py-2 text-left text-sm transition-colors",
                  active
                    ? "bg-nav-active font-medium text-accent"
                    : "text-text-secondary hover:bg-nav-hover hover:text-text-primary"
                )}
              >
                {chat.is_default ? "# " : ""}
                {chat.name || "Channel"}
                {chat.is_default ? (
                  <Badge variant="outline" className="ml-2 font-normal">
                    Default
                  </Badge>
                ) : null}
              </button>
            );
          })}
        </nav>

        <div className="min-w-0 rounded-lg border border-ui-border bg-ui-card p-4">
          {!selectedChat ? (
            <p className="text-sm text-text-muted">Select a channel.</p>
          ) : (
            <>
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-text-primary">
                    {selectedChat.name}
                  </h3>
                  <p className="text-xs text-text-muted">
                    {selectedChat.member_count || members.length} members
                    {typeof selectedChat.active_now_count === "number"
                      ? ` · ${selectedChat.active_now_count} active now`
                      : ""}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {!selectedChat.is_default ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={busy}
                      onClick={() => setShowAdd((v) => !v)}
                    >
                      Add members
                    </Button>
                  ) : null}
                  {!selectedChat.is_default ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      disabled={busy}
                      onClick={() => setConfirmDeleteChat(selectedChat)}
                    >
                      Delete
                    </Button>
                  ) : null}
                </div>
              </div>

              {showAdd && !selectedChat.is_default ? (
                <div className="mb-4 overflow-hidden rounded-md border border-ui-border">
                  <MultiUserSearch
                    userId={orgId}
                    onSubmit={handleAddMembers}
                    excludeUsers={members.map((m) => ({ id: m.user_id }))}
                    submitText="Add Members"
                    candidateUsers={addCandidates}
                  />
                </div>
              ) : null}

              <ul className="divide-y divide-ui-border">
                {members.length === 0 ? (
                  <li className="py-4 text-sm text-text-muted">No members in this channel.</li>
                ) : (
                  members.map((m) => (
                    <li
                      key={m.user_id || m.id}
                      className="flex items-center justify-between gap-3 py-2.5"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-text-primary">
                          {m.name || m.username || `User ${m.user_id}`}
                        </div>
                        {m.role ? (
                          <div className="text-xs capitalize text-text-muted">{m.role}</div>
                        ) : null}
                      </div>
                      {!selectedChat.is_default ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="text-delete"
                          disabled={busy}
                          onClick={() => setConfirmRemoveMember(m)}
                        >
                          Remove
                        </Button>
                      ) : null}
                    </li>
                  ))
                )}
              </ul>
            </>
          )}
        </div>
      </div>

      {showNew ? (
        <NewOrgChannelModal
          userId={orgId}
          orgId={orgId}
          candidateUsers={candidateUsers}
          asOrgAdmin
          onClose={() => setShowNew(false)}
          onChatCreated={() => {
            setShowNew(false);
            load();
          }}
        />
      ) : null}

      <Dialog
        open={!!confirmDeleteChat}
        onOpenChange={(open) => {
          if (!open && !busy) setConfirmDeleteChat(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete channel?</DialogTitle>
            <DialogDescription>
              {confirmDeleteChat
                ? `This permanently deletes “${confirmDeleteChat.name || "this channel"}” and all of its messages. This cannot be undone.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => setConfirmDeleteChat(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={busy}
              onClick={handleConfirmDelete}
            >
              {busy ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!confirmRemoveMember}
        onOpenChange={(open) => {
          if (!open && !busy) setConfirmRemoveMember(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remove member?</DialogTitle>
            <DialogDescription>
              {confirmRemoveMember
                ? `Remove ${removeMemberName} from “${selectedChat?.name || "this channel"}”? They will no longer see messages in this channel.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => setConfirmRemoveMember(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={busy}
              onClick={handleConfirmRemoveMember}
            >
              {busy ? "Removing…" : "Remove"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ChannelsTab;
